class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* === OATENTIK cart drawer extras ==========================================
   Pack switcher, subscribe & save upgrade, FBT / bigger-pack ATC.
   Document-level event delegation: handlers survive Section Rendering API
   re-renders (Dawn replaces drawer innerHTML on every cart change). */
(() => {
  if (window.oatCartExtrasLoaded) return;
  window.oatCartExtrasLoaded = true;

  const SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  let oatCartBusy = false;

  const oatRoutes = () =>
    window.routes || { cart_add_url: '/cart/add', cart_change_url: '/cart/change' };

  const trackEvent = (name, payload) => {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, payload || {});
      }
    } catch (error) {
      /* analytics must never break the cart */
    }
  };

  const cartConfig = () =>
    typeof fetchConfig === 'function'
      ? fetchConfig()
      : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };

  const postCart = (url, payload) =>
    fetch(url, { ...cartConfig(), body: JSON.stringify(payload) }).then((response) => response.json());

  const setBusy = (state) => {
    oatCartBusy = state;
    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.classList.toggle('oat-cart-busy', state);
  };

  const refreshDrawer = (parsedState) => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || !parsedState || !parsedState.sections) return;
    drawer.classList.toggle('is-empty', parsedState.item_count === 0);
    drawer.renderContents(parsedState);
  };

  const assertOk = (state) => {
    if (state && state.status && state.status !== 200) {
      throw new Error(state.description || state.message || 'Cart request failed');
    }
    return state;
  };

  /* Pack switcher: add the new variant (same qty / selling plan), then remove
     the old line by key (keys are stable across the add; indexes are not). */
  document.addEventListener('change', (event) => {
    if (!(event.target instanceof Element)) return;
    const select = event.target.closest('.cart-drawer__pack-select');
    if (!select) return;
    if (oatCartBusy) {
      select.value = select.dataset.currentVariant;
      return;
    }
    const newVariantId = Number(select.value);
    const oldVariantId = Number(select.dataset.currentVariant);
    if (!newVariantId || newVariantId === oldVariantId) return;

    const quantity = Number(select.dataset.quantity) || 1;
    const lineKey = select.dataset.lineKey;
    const sellingPlan = select.dataset.sellingPlan ? Number(select.dataset.sellingPlan) : null;
    const routes = oatRoutes();

    setBusy(true);
    select.disabled = true;

    const addPayload = { id: newVariantId, quantity };
    if (sellingPlan) addPayload.selling_plan = sellingPlan;

    postCart(routes.cart_add_url, addPayload)
      .then(assertOk)
      .then(() =>
        postCart(routes.cart_change_url, {
          id: lineKey,
          quantity: 0,
          sections: SECTIONS,
          sections_url: window.location.pathname,
        })
      )
      .then(assertOk)
      .then((state) => {
        refreshDrawer(state);
        trackEvent('oat_cart_pack_upsell_atc', {
          source: 'pack_switcher',
          variantId: newVariantId,
          previousVariantId: oldVariantId,
          quantity,
        });
      })
      .catch(() => {
        select.value = String(oldVariantId);
        select.disabled = false;
      })
      .finally(() => setBusy(false));
  });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    /* FBT strip + "bigger packs" panel ATC are handled by the single inline
       delegated handler in cart-drawer.liquid, which also injects the free gifts.
       A second [data-oat-cart-add] handler here double-added every upsell item
       (one tap -> 2 in cart), so it has been removed. */

    /* subscribe & save: toggle the frequency popover */
    const subToggle = event.target.closest('[data-oat-sub-toggle]');
    if (subToggle) {
      event.preventDefault();
      const pop = subToggle.closest('.cart-drawer__sub')?.querySelector('.cart-drawer__sub-pop');
      if (pop) {
        const willShow = pop.hasAttribute('hidden');
        if (willShow) pop.removeAttribute('hidden');
        else pop.setAttribute('hidden', '');
        subToggle.setAttribute('aria-expanded', String(willShow));
        if (willShow) trackEvent('oat_cart_subscribe_open', {});
      }
      return;
    }

    /* one-time -> subscription upgrade on the same line, chosen frequency */
    const subOption = event.target.closest('.cart-drawer__sub-option');
    if (subOption) {
      event.preventDefault();
      if (oatCartBusy) return;
      const sellingPlan = Number(subOption.dataset.sellingPlan);
      if (!sellingPlan) return;
      setBusy(true);
      subOption.disabled = true;
      postCart(oatRoutes().cart_change_url, {
        id: subOption.dataset.lineKey,
        quantity: Number(subOption.dataset.quantity) || 1,
        selling_plan: sellingPlan,
        sections: SECTIONS,
        sections_url: window.location.pathname,
      })
        .then(assertOk)
        .then((state) => {
          refreshDrawer(state);
          trackEvent('oat_cart_subscribe_upgrade', { sellingPlanId: sellingPlan });
        })
        .catch(() => {
          subOption.disabled = false;
        })
        .finally(() => setBusy(false));
    }
  });
})();

